import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import { alpha, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { TUploadedFile } from '@onesource/schemas';
import { useDeleteMediaFileMutation } from '@onesource/services';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { MdCheckCircle, MdContentCopy, MdDeleteOutline, MdPictureAsPdf, MdZoomIn } from 'react-icons/md';

import { useConfirmService } from '../dialogs';
import { CopyUrlMenu } from './copy-url-menu';
import { MediaPreviewDialog } from './media-preview-dialog';

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SKELETON_ROWS = 6;
const COL_SPAN = 7;

function TableSkeleton() {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <TableRow key={i}>
          <TableCell sx={{ width: 56, py: 0.75 }}>
            <Skeleton variant="rectangular" width={44} height={44} sx={{ borderRadius: 1 }} />
          </TableCell>
          <TableCell>
            <Skeleton width="60%" />
          </TableCell>
          <TableCell>
            <Skeleton width={80} />
          </TableCell>
          <TableCell>
            <Skeleton width={60} />
          </TableCell>
          <TableCell>
            <Skeleton width={48} />
          </TableCell>
          <TableCell>
            <Skeleton width={80} />
          </TableCell>
          <TableCell align="right">
            <Skeleton width={64} sx={{ ml: 'auto' }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export type TMediaTableProps = {
  files: TUploadedFile[];
  isLoading: boolean;
};

export function MediaTable({ files, isLoading }: TMediaTableProps) {
  const theme = useTheme();
  const [previewFile, setPreviewFile] = useState<TUploadedFile | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const { setConfirm } = useConfirmService();
  const { mutateAsync: deleteMediaFileAsync } = useDeleteMediaFileMutation();

  // Drop stale selection when the underlying page/filter changes.
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [files]);

  const handleCopyUrl = useCallback((url: string) => {
    navigator.clipboard.writeText(url);
    enqueueSnackbar('URL copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
  }, []);

  const handleClosePreview = useCallback(() => setPreviewFile(null), []);
  const handleNavigate = useCallback((file: TUploadedFile) => setPreviewFile(file), []);

  const toggleSelect = useCallback((key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(() => setSelectedKeys(new Set()), []);

  const handleBulkDelete = useCallback(() => {
    const keys = [...selectedKeys];
    if (keys.length === 0) {
      return;
    }
    setConfirm({
      open: true,
      title: 'Delete media files',
      message: `Delete ${keys.length} selected file${keys.length > 1 ? 's' : ''}? This cannot be undone.`,
      confirmBtnLabel: 'Delete permanently',
      onConfirm: async () => {
        const results = await Promise.allSettled(keys.map(key => deleteMediaFileAsync(key)));
        const failed = results.filter(result => result.status === 'rejected').length;
        if (failed > 0) {
          enqueueSnackbar(`${failed} file(s) failed to delete`, { variant: 'error' });
        } else {
          enqueueSnackbar(`${keys.length} file(s) deleted`, { variant: 'success' });
        }
        if (previewFile && keys.includes(previewFile.key)) {
          setPreviewFile(null);
        }
        setSelectedKeys(new Set());
      },
    });
  }, [selectedKeys, setConfirm, deleteMediaFileAsync, previewFile]);

  const headers = (
    <TableRow>
      <TableCell>Preview</TableCell>
      <TableCell>Name</TableCell>
      <TableCell>MIME type</TableCell>
      <TableCell>Ext</TableCell>
      <TableCell>Size</TableCell>
      <TableCell>Uploaded</TableCell>
      <TableCell align="right">Actions</TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      {selectedKeys.size > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.5,
            py: 1,
            mb: 1,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {selectedKeys.size} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="soft" onClick={handleClearSelection}>
            Clear
          </Button>
          <Button
            size="small"
            variant="soft"
            color="error"
            startIcon={<MdDeleteOutline size={18} />}
            onClick={handleBulkDelete}
          >
            Delete selected
          </Button>
        </Box>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>{headers}</TableHead>
          <TableBody>
            {files.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={COL_SPAN} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No files uploaded yet. Click "Upload" to add files.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              files.map(file => {
                const isPdf = file.mimeType === 'application/pdf';
                const ext = file.filename.split('.').pop()?.toLowerCase() ?? '';
                const isSelected = selectedKeys.has(file.key);
                return (
                  <TableRow
                    key={file.key}
                    hover
                    sx={{ bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : undefined }}
                  >
                    {/* Thumbnail — click selects the row */}
                    <TableCell sx={{ width: 56, py: 0.75 }}>
                      <ButtonBase
                        onClick={() => toggleSelect(file.key)}
                        aria-label={`Select ${file.filename}`}
                        sx={{ display: 'block', borderRadius: 1, position: 'relative' }}
                      >
                        {isPdf ? (
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 1,
                              bgcolor: 'action.hover',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <MdPictureAsPdf size={24} color="#e53935" />
                          </Box>
                        ) : file.publicUrl ? (
                          <Box
                            component="img"
                            src={file.publicUrl}
                            alt={file.filename}
                            sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, display: 'block' }}
                          />
                        ) : (
                          <Box sx={{ width: 44, height: 44, borderRadius: 1, bgcolor: 'action.hover' }} />
                        )}

                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.primary.main, 0.25),
                            }}
                          >
                            <Box
                              sx={{
                                color: theme.palette.primary.main,
                                bgcolor: 'background.paper',
                                borderRadius: '50%',
                                lineHeight: 0,
                                boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.3)}`,
                              }}
                            >
                              <MdCheckCircle size={18} />
                            </Box>
                          </Box>
                        )}
                      </ButtonBase>
                    </TableCell>

                    {/* Name — click opens the preview popup */}
                    <TableCell>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        underline="hover"
                        color="text.primary"
                        onClick={() => setPreviewFile(file)}
                        sx={{ fontWeight: 500, textAlign: 'left' }}
                      >
                        {file.filename}
                      </Link>
                    </TableCell>

                    {/* MIME type */}
                    <TableCell>
                      <Chip label={file.mimeType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    </TableCell>

                    {/* Extension */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.75rem' }}
                      >
                        {ext}
                      </Typography>
                    </TableCell>

                    {/* Size */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatBytes(file.size)}
                      </Typography>
                    </TableCell>

                    {/* Uploaded */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(file.lastModified).toLocaleDateString()}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Preview" arrow>
                        <IconButton onClick={() => setPreviewFile(file)}>
                          <MdZoomIn size={20} />
                        </IconButton>
                      </Tooltip>
                      {file.cdnUrls && Object.keys(file.cdnUrls).length > 0 ? (
                        <CopyUrlMenu cdnUrls={file.cdnUrls} variantSizes={{}} standalone iconSize={20} />
                      ) : (
                        file.publicUrl && (
                          <Tooltip title="Copy URL" arrow>
                            <IconButton onClick={() => handleCopyUrl(file.publicUrl!)}>
                              <MdContentCopy size={20} />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Overlay skeleton while loading */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            bgcolor: theme => theme.palette.background.default,
            borderRadius: 1,
          }}
        >
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>{headers}</TableHead>
              <TableBody>
                <TableSkeleton />
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <MediaPreviewDialog file={previewFile} onClose={handleClosePreview} files={files} onNavigate={handleNavigate} />
    </Box>
  );
}
